
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Link as LinkIcon, Clock, User } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export function ProviderManagement() {
  const { toast } = useToast();
  const [providers, setProviders] = useState([
    {
      id: 1,
      name: "Dr. Sarah Smith",
      specialty: "Cardiology",
      email: "sarah.smith@clinic.com",
      phone: "(555) 123-4567",
      status: "active",
      bookingLink: "https://clinicaflow.pro/book/dr-sarah-smith",
      workingHours: "9:00 AM - 5:00 PM",
    },
    {
      id: 2,
      name: "Dr. Michael Wilson",
      specialty: "Dermatology",
      email: "michael.wilson@clinic.com",
      phone: "(555) 987-6543",
      status: "active",
      bookingLink: "https://clinicaflow.pro/book/dr-michael-wilson",
      workingHours: "10:00 AM - 6:00 PM",
    },
    {
      id: 3,
      name: "Dr. Emily Johnson",
      specialty: "Pediatrics",
      email: "emily.johnson@clinic.com",
      phone: "(555) 456-7890",
      status: "inactive",
      bookingLink: "https://clinicaflow.pro/book/dr-emily-johnson",
      workingHours: "8:00 AM - 4:00 PM",
    },
  ]);

  const [newProvider, setNewProvider] = useState({
    name: "",
    specialty: "",
    email: "",
    phone: "",
    workingHours: "",
  });

  const handleAddProvider = () => {
    if (newProvider.name && newProvider.specialty && newProvider.email) {
      const provider = {
        id: providers.length + 1,
        ...newProvider,
        status: "active",
        bookingLink: `https://clinicaflow.pro/book/${newProvider.name.toLowerCase().replace(/\s+/g, '-')}`,
      };
      setProviders([...providers, provider]);
      setNewProvider({ name: "", specialty: "", email: "", phone: "", workingHours: "" });
      toast({
        title: "Provider Added",
        description: "New provider has been successfully added to the system.",
      });
    }
  };

  const copyBookingLink = (link: string) => {
    navigator.clipboard.writeText(link);
    toast({
      title: "Link Copied",
      description: "Booking link has been copied to clipboard.",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Provider Management</h1>
          <p className="text-gray-600">Manage your healthcare providers and their booking links.</p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Add Provider
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Provider</DialogTitle>
              <DialogDescription>
                Add a new healthcare provider to your clinic management system.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={newProvider.name}
                  onChange={(e) => setNewProvider({ ...newProvider, name: e.target.value })}
                  placeholder="Dr. John Doe"
                />
              </div>
              <div>
                <Label htmlFor="specialty">Specialty</Label>
                <Select onValueChange={(value) => setNewProvider({ ...newProvider, specialty: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cardiology">Cardiology</SelectItem>
                    <SelectItem value="dermatology">Dermatology</SelectItem>
                    <SelectItem value="pediatrics">Pediatrics</SelectItem>
                    <SelectItem value="neurology">Neurology</SelectItem>
                    <SelectItem value="orthopedics">Orthopedics</SelectItem>
                    <SelectItem value="general">General Practice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newProvider.email}
                  onChange={(e) => setNewProvider({ ...newProvider, email: e.target.value })}
                  placeholder="doctor@clinic.com"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={newProvider.phone}
                  onChange={(e) => setNewProvider({ ...newProvider, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <Label htmlFor="hours">Working Hours</Label>
                <Input
                  id="hours"
                  value={newProvider.workingHours}
                  onChange={(e) => setNewProvider({ ...newProvider, workingHours: e.target.value })}
                  placeholder="9:00 AM - 5:00 PM"
                />
              </div>
              <Button onClick={handleAddProvider} className="w-full">
                Add Provider
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {providers.map((provider) => (
          <Card key={provider.id} className="hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{provider.name}</CardTitle>
                    <CardDescription>{provider.specialty}</CardDescription>
                  </div>
                </div>
                <Badge variant={provider.status === "active" ? "default" : "secondary"}>
                  {provider.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Email:</span>
                  <span className="text-gray-600">{provider.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Phone:</span>
                  <span className="text-gray-600">{provider.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">{provider.workingHours}</span>
                </div>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Booking Link</p>
                    <p className="text-xs text-gray-500 truncate max-w-[200px]">{provider.bookingLink}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyBookingLink(provider.bookingLink)}
                  >
                    <LinkIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button variant="outline" size="sm" className="flex-1">
                  Schedule
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
